// src/lib/forms-migration.js — ADR-074
// Helpers de migration lazy du format formulaires unifiés. Partagés
// entre FormulairesTab (admin) et PublicFormPage (voyageur) pour
// garantir cohérence de comportement même si l'admin n'a jamais cliqué
// Enregistrer (cfg en base reste au format welcome+checkin legacy).
//
// 3 helpers idempotents appliqués en cascade :
//   - removeLegacyDateFields : retire les anciens fields date checkin/checkout
//     (ADR-072, dates viennent désormais de la résa Beds24 read-only)
//   - ensureRequestFields : ajoute early_checkin_request + late_checkout_request
//     (ADR-072, demandes spécifiques optionnelles)
//   - ensureIdTypeField : ajoute id_type radio avant passport_photo
//     (ADR-071, choix conditionnel pièce d'identité)

// Décision produit (Dimitri) : le champ libre « Commentaires » (reference
// 'comments') est RETIRÉ du formulaire de bienvenue par défaut. Un champ libre
// laissait attendre une gratuité sur de futurs services payants (arrivée
// anticipée / départ tardif) : ces demandes passent désormais par les services
// payants du livret, jamais par un canal libre ici.
// (Le raisonnement ADR-075 — « le champ comments suffit pour ces demandes » —
// est ainsi annulé ; le retrait ADR-072 des champs early/late reste annulé,
// cf. removeRequestFields plus bas.)
export const WELCOME_DEFAULT_FIELDS = [
  { id: 'f1',  label: 'Email',                       type: 'input',    input_type: 'email', required: true,  reference: 'email' },
  { id: 'f2',  label: 'Téléphone',                   type: 'input',    input_type: 'tel',   required: true,  reference: 'phone' },
  { id: 'f5',  label: "Heure d'arrivée",             type: 'time',                          required: true,  reference: 'time_in' },
  { id: 'f6',  label: 'Heure de départ',             type: 'time',                          required: true,  reference: 'time_out' },
  { id: 'f8',  label: 'Notes',                       type: 'textarea',                      required: false, reference: 'notes' },
]

export const CHECKIN_DEFAULT_FIELDS = [
  // Décision produit 2026-06-12 (annule « pièce unique ») : choix EXPLICITE du
  // type de pièce. Le radio id_type pilote l'affichage conditionnel côté
  // PublicFormPage (shouldRenderField) :
  //   - Passeport → 1 upload « Page d'identité du passeport » (passport_photo)
  //   - Carte d'identité → 2 uploads OBLIGATOIRES « Recto » + « Verso »
  // Le titre de groupe « Pièce d'identité de la personne qui séjournera… » est
  // rendu côté PublicFormPage (regroupement visuel des 4 champs ci-dessous).
  { id: 'c0', label: "Type de pièce d'identité",      type: 'radio',    required: true, reference: 'id_type', options: ['Passeport', "Carte d'identité"] },
  { id: 'c1', label: "Page d'identité du passeport",  type: 'file',     required: true, reference: 'passport_photo' },
  { id: 'c2', label: 'Recto',                         type: 'file',     required: true, reference: 'id_recto' },
  { id: 'c3', label: 'Verso',                         type: 'file',     required: true, reference: 'id_verso' },
  { id: 'c7', label: 'Adresse facturation',           type: 'textarea', required: true, reference: 'billing_address' },
]

export const UNIFIED_DEFAULT = {
  // ADR-075 fix : title sans " - {logement_name}" (le H1 hero affiche déjà
  // le nom du logement, doublon visuel inutile).
  title: 'Formulaire d\'accueil',
  description: "Merci de remplir ce formulaire pour préparer votre arrivée. À bientôt, Néïsia & Dimitri - Fullbnbzen",
  redirect_url: '',
  // Couverture = ADRESSE dans le stockage (src/lib/form-cover-photo.js) ;
  // `cover_photo_b64` n'est plus qu'un vestige lu en compatibilité.
  cover_photo_url: null,
  cover_photo_path: null,
  cover_photo_b64: null,
  fields: [...WELCOME_DEFAULT_FIELDS, ...CHECKIN_DEFAULT_FIELDS],
}

// ── ADR-189 amendement (test visuel Vague 1) — pièce d'identité exigible
// PAR LOGEMENT ET PAR CANAL ────────────────────────────────────────────────
// Stocké dans la config forms du logement (même blob que forms.unified) sous la
// clé `id_doc_policy = { direct, booking, airbnb }` (3 booléens). Surcharge par
// logement, défauts si absent :
//   DIRECT = requise · BOOKING = requise · AIRBNB = désactivée
// (Airbnb vérifie déjà l'identité du voyageur → module pièce inutile).
//
// Aligné sur le précédent caution PAR PLATEFORME (lib/cfg-reader-server.js
// _CAUTION_PLATFORM_DEFAULTS) : Direct + Booking par défaut actifs, Airbnb à part.
export const ID_DOC_POLICY_DEFAULTS = { direct: true, booking: true, airbnb: false }

// Normalise un libellé de source résa vers une clé de canal pièce d'identité.
// Aligné sur _normalizeCautionPlatform (cfg-reader-server) + variants booking
// legacy iCal V1. Source inconnue → 'direct' (un token sans résa identifiée est
// traité comme direct, cf. spec form-config).
export function normalizeIdDocChannel(source) {
  const s = String(source || '').toLowerCase()
  if (s === 'airbnb') return 'airbnb'
  if (s === 'booking' || s === 'booking_com' || s === 'bookingdotcom') return 'booking'
  return 'direct'
}

// Retourne la policy pièce d'identité effective du logement (3 booléens),
// fusionnée avec les défauts. Tolère forms absent / partiel / clés extra.
export function getIdDocPolicy(forms) {
  const raw = (forms && typeof forms === 'object' && forms.id_doc_policy && typeof forms.id_doc_policy === 'object')
    ? forms.id_doc_policy
    : {}
  return {
    direct: typeof raw.direct === 'boolean' ? raw.direct : ID_DOC_POLICY_DEFAULTS.direct,
    booking: typeof raw.booking === 'boolean' ? raw.booking : ID_DOC_POLICY_DEFAULTS.booking,
    airbnb: typeof raw.airbnb === 'boolean' ? raw.airbnb : ID_DOC_POLICY_DEFAULTS.airbnb,
  }
}

// La pièce d'identité est-elle exigée pour ce (logement, canal) ? Le canal est
// une source résa brute (normalisée ici) OU une clé déjà normalisée.
export function isIdDocRequired(forms, source) {
  const channel = normalizeIdDocChannel(source)
  return !!getIdDocPolicy(forms)[channel]
}

// ── Décision produit 2026-09-06 — adresse de facturation PAR CANAL ──────────
// « L'adresse de facturation Airbnb : retire-la. Airbnb facture, je n'ai aucun
// usage de cette adresse. » (Dimitri). MÊME MÉCANIQUE que id_doc_policy
// (ADR-189 amendement) : clé `forms.billing_address_policy = { direct, booking,
// airbnb }` (surcharge par logement possible), défauts si absente :
//   DIRECT = demandée · BOOKING = demandée · AIRBNB = retirée.
// Fail-safe identique : source inconnue/absente → 'direct' → champ conservé.
export const BILLING_ADDRESS_POLICY_DEFAULTS = { direct: true, booking: true, airbnb: false }

// Retourne la policy adresse de facturation effective du logement (3 booléens),
// fusionnée avec les défauts. Tolère forms absent / partiel / clés extra.
export function getBillingAddressPolicy(forms) {
  const raw = (forms && typeof forms === 'object' && forms.billing_address_policy && typeof forms.billing_address_policy === 'object')
    ? forms.billing_address_policy
    : {}
  return {
    direct: typeof raw.direct === 'boolean' ? raw.direct : BILLING_ADDRESS_POLICY_DEFAULTS.direct,
    booking: typeof raw.booking === 'boolean' ? raw.booking : BILLING_ADDRESS_POLICY_DEFAULTS.booking,
    airbnb: typeof raw.airbnb === 'boolean' ? raw.airbnb : BILLING_ADDRESS_POLICY_DEFAULTS.airbnb,
  }
}

// L'adresse de facturation est-elle demandée pour ce (logement, canal) ?
// Même normalisation de canal que la pièce d'identité (une seule cascade).
export function isBillingAddressRequired(forms, source) {
  return !!getBillingAddressPolicy(forms)[normalizeIdDocChannel(source)]
}

// ── TITRE DE SÉJOUR — 3e type de document (décision 3, 2026-09-06) ─────────
// « Mes voyageurs ne sont pas tous européens. » Recto + verso comme la CNI
// (la validité est au recto, la bande MRZ au verso — une seule face perd de
// l'information) : le rail recto/verso existant se réutilise tel quel.
//
// VALEUR CANONIQUE FR « Titre de séjour » — JAMAIS traduite (piège id_type,
// CLAUDE.md : l'affichage conditionnel compare la valeur FR ; le libellé
// d'affichage EN/ES passe par pieceOptionLabel).
//
// ⚠️ AMENDEMENT ADR-075 ASSUMÉ (verdict Q-CAS1, Dimitri 2026-09-06 : « La
// pièce d'identité est un module de la maison, pas un réglage d'agrément.
// Documente l'exception. ») — ce helper idempotent s'applique à TOUS les cas
// de migrateLegacyToUnified, Y COMPRIS le Cas 1 (unified.fields explicite
// admin) qu'ADR-075 déclarait intouchable. Sans lui, l'option « Titre de
// séjour » ne serait JAMAIS proposée aux voyageurs des logements déjà
// configurés (J1 : feature absente à l'écran — l'upload, lui, passerait, les
// gardes serveur étant globales). Périmètre chirurgical : il n'ajoute QUE
// l'option TS + les 2 champs ts_recto/ts_verso, jamais il ne retire ni ne
// réécrit quoi que ce soit. Détail : decisions-log.md (ADR du lot).
export const ID_TYPE_TS = 'Titre de séjour'
const TS_RECTO_FIELD = { id: 'c8', label: 'Recto du titre de séjour', type: 'file', required: true, reference: 'ts_recto' }
const TS_VERSO_FIELD = { id: 'c9', label: 'Verso du titre de séjour', type: 'file', required: true, reference: 'ts_verso' }

export function ensureTitreSejourFields(fields) {
  if (!Array.isArray(fields)) return fields
  const idTypeIdx = fields.findIndex((f) => f && f.reference === 'id_type')
  // Pas de module pièce dans ce form → rien à injecter (un form welcome-only
  // ne reçoit pas d'uploads TS orphelins).
  if (idTypeIdx < 0) return fields
  let next = fields
  const idType = fields[idTypeIdx]
  if (Array.isArray(idType.options) && !idType.options.includes(ID_TYPE_TS)) {
    next = [...next]
    next[idTypeIdx] = { ...idType, options: [...idType.options, ID_TYPE_TS] }
  }
  const hasRecto = next.some((f) => f && f.reference === 'ts_recto')
  const hasVerso = next.some((f) => f && f.reference === 'ts_verso')
  if (hasRecto && hasVerso) return next
  // Insertion APRÈS le dernier champ upload du module pièce (id_verso, sinon
  // passport_photo, sinon id_type) — les champs TS restent dans le groupe.
  const anchors = ['id_verso', 'passport_photo', 'id_type']
  let insertAt = next.length
  for (const a of anchors) {
    const i = next.findIndex((f) => f && f.reference === a)
    if (i >= 0) { insertAt = i + 1; break }
  }
  const toInsert = []
  if (!hasRecto) toInsert.push({ ...TS_RECTO_FIELD })
  if (!hasVerso) toInsert.push({ ...TS_VERSO_FIELD })
  return [...next.slice(0, insertAt), ...toInsert, ...next.slice(insertAt)]
}

export function ensureIdTypeField(fields) {
  if (!Array.isArray(fields)) return fields
  if (fields.some(f => f.reference === 'id_type')) return fields
  const idTypeField = { id: 'c0', label: "Type de pièce d'identité", type: 'radio', required: true, reference: 'id_type', options: ['Passeport', "Carte d'identité"] }
  const passportIdx = fields.findIndex(f => f.reference === 'passport_photo')
  const insertAt = passportIdx >= 0 ? passportIdx : fields.length
  return [...fields.slice(0, insertAt), idTypeField, ...fields.slice(insertAt)]
}

export function removeLegacyDateFields(fields) {
  if (!Array.isArray(fields)) return fields
  return fields.filter(f => !(f.type === 'date' && (f.reference === 'checkin' || f.reference === 'checkout')))
}

// ADR-075 — annule ADR-072 ensureRequestFields. Helper symétrique à
// removeLegacyDateFields : retire les anciens champs early_checkin_request
// et late_checkout_request des cfg existantes (ceux qui ont été ajoutés
// par ADR-072 entre temps). Idempotent, appliqué uniquement sur les
// branches de migration (cas legacy → unified, pas sur unified.fields
// existant — l'admin garde la maîtrise s'il les avait conservés
// volontairement après ADR-072).
export function removeRequestFields(fields) {
  if (!Array.isArray(fields)) return fields
  return fields.filter(f => f.reference !== 'early_checkin_request' && f.reference !== 'late_checkout_request')
}

// Retrait du champ libre « Commentaires » (reference 'comments') des cfg
// legacy en migration. Symétrique à removeRequestFields : idempotent, appliqué
// UNIQUEMENT sur les branches legacy → unified (Cas 2/3). Un unified.fields
// explicite admin (Cas 1) n'est JAMAIS touché — l'admin garde la maîtrise de sa
// config (cf. migrateLegacyToUnified). Le retrait du champ de SAISIE : les
// anciennes soumissions déjà enregistrées (payload avec `comments`) restent
// affichables côté admin — voir FormSubmissionView (rendu générique).
export function removeCommentsField(fields) {
  if (!Array.isArray(fields)) return fields
  return fields.filter(f => f.reference !== 'comments')
}

// migrateLegacyToUnified — 3 cas + helpers ensure* appliqués UNIQUEMENT
// sur les branches de construction (pas sur le cas "admin a déjà un
// unified.fields explicite", ADR-075 fix bug suppression label).
//   1. forms.unified.fields existe (nouveau format ADR-071) → return tel quel,
//      pas d'ensure* (l'admin a la maîtrise complète des fields)
//   2. forms.unified.sections existe (ancien format ADR-065) → flatten en
//      fields[] + ensure* (migration unique depuis legacy)
//   3. ni l'un ni l'autre, mais forms.welcome ou forms.checkin existent →
//      fusion + ensure*
//   4. rien : default complet (déjà fields propres, pas d'ensure*)
// Idempotent. Pas de save auto — préserve les tokens publics déjà distribués.
// Le save intervient au premier édit admin.
export function migrateLegacyToUnified(forms) {
  const f = forms || {}
  if (f.unified && Array.isArray(f.unified.fields)) {
    // Cas 1 : admin a déjà un format unified explicite — respecter sa
    // configuration. NE PAS réinjecter de champs (ADR-075 — sinon
    // suppression label admin annulée silencieusement au remount).
    // ⚠️ EXCEPTION UNIQUE ET ASSUMÉE (amendement ADR-075, verdict Q-CAS1) :
    // ensureTitreSejourFields s'applique AUSSI ici — le titre de séjour est un
    // module de la maison, pas un réglage d'agrément. Idempotent, additif
    // (option TS + ts_recto/ts_verso), ne touche à rien d'autre.
    return { ...f.unified, fields: ensureTitreSejourFields(f.unified.fields) }
  }
  let result
  if (f.unified && Array.isArray(f.unified.sections)) {
    // Cas 2 : ancien format avec sections — migration unique
    const { sections, ...rest } = f.unified
    result = { ...rest, fields: sections.flatMap(s => s.fields || []) }
  } else if (f.unified) {
    // Cas 2bis : objet unified présent mais ni fields ni sections (corrupt) →
    // fallback default fields
    result = { ...f.unified, fields: [...UNIFIED_DEFAULT.fields] }
  } else if (f.welcome || f.checkin) {
    // Cas 3 : fusion welcome+checkin legacy
    result = {
      title: f.welcome?.title || f.checkin?.title || UNIFIED_DEFAULT.title,
      description: f.welcome?.description || UNIFIED_DEFAULT.description,
      redirect_url: f.welcome?.redirect_url || f.checkin?.redirect_url || '',
      cover_photo_url: f.welcome?.cover_photo_url || f.checkin?.cover_photo_url || null,
      cover_photo_path: f.welcome?.cover_photo_path || f.checkin?.cover_photo_path || null,
      cover_photo_b64: f.welcome?.cover_photo_b64 || f.checkin?.cover_photo_b64 || null,
      fields: [
        ...(f.welcome?.fields || WELCOME_DEFAULT_FIELDS),
        ...(f.checkin?.fields || CHECKIN_DEFAULT_FIELDS),
      ],
    }
  } else {
    // Cas 4 : pas de cfg formulaires du tout — default complet. Le titre de
    // séjour s'injecte par le MÊME helper que partout (les défauts constants
    // restent à 2 options — source unique de l'injection TS).
    return { ...UNIFIED_DEFAULT, fields: ensureTitreSejourFields([...UNIFIED_DEFAULT.fields]) }
  }
  // ensure* + remove* uniquement sur les cas legacy → unified ; le titre de
  // séjour (ensureTitreSejourFields) s'applique lui à TOUS les cas (Q-CAS1).
  return { ...result, fields: ensureTitreSejourFields(ensureIdTypeField(removeCommentsField(removeRequestFields(removeLegacyDateFields(result.fields))))) }
}
